import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Shield,
  Loader2,
  Check,
  Copy,
  KeyRound,
  Smartphone,
  AlertTriangle,
  LogOut,
  Lock,
  Mail,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useAuthStore } from '@/store/auth.store';
import {
  useSetupTwoFactor,
  useEnableTwoFactor,
  useComplete2FASetup,
  useSetSecurityMethod,
} from '@/hooks/useAuth';
import { TwoFactorVerifyData } from '@/types/auth.types';

const verificationSchema = z.object({
  token: z
    .string()
    .length(6, 'auth.validation.twoFactor.tokenLength')
    .regex(/^\d+$/, 'auth.validation.twoFactor.tokenDigits'),
});

type FormData = z.infer<typeof verificationSchema>;

/**
 * Force2FASetupPage
 *
 * A full-page experience for vault users who must set up 2FA before accessing
 * any other part of the application. This page is shown when:
 * - User has `requires2FASetup: true`
 * - User has not yet enabled 2FA
 */
export const Force2FASetupPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  const [selectedMethod, setSelectedMethod] = useState<'totp' | 'otp' | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);

  const {
    mutate: setupMutate,
    isPending: isSetupPending,
    isError: isSetupError,
  } = useSetupTwoFactor();
  const enableTwoFactor = useEnableTwoFactor();
  const complete2FASetup = useComplete2FASetup();
  const setSecurityMethod = useSetSecurityMethod();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(verificationSchema),
  });

  // Setup TOTP when method is chosen
  useEffect(() => {
    if (selectedMethod === 'totp' && !qrCode) {
      setupMutate(undefined, {
        onSuccess: (response) => {
          setQrCode(response.data.qrCode);
          setSecret(response.data.secret);
        },
      });
    }
  }, [selectedMethod, setupMutate, qrCode]);

  const handleChooseOtp = async () => {
    try {
      await setSecurityMethod.mutateAsync('otp');
      await complete2FASetup.mutateAsync();
      if (user?.pendingVaultId) {
        navigate(`/vault/initialize/${user.pendingVaultId}`);
      } else {
        navigate('/my-vaults');
      }
    } catch {
      // Error handled by hooks
    }
  };

  const handleRetrySetup = () => {
    setupMutate(undefined, {
      onSuccess: (response) => {
        setQrCode(response.data.qrCode);
        setSecret(response.data.secret);
      },
    });
  };

  const handleCopySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success(t('settings.twoFactor.secretCopied'));
    }
  };

  const onSubmit = async (data: FormData) => {
    try {
      const verifyData: TwoFactorVerifyData = {
        token: data.token,
      };

      // Enable 2FA
      await enableTwoFactor.mutateAsync(verifyData);

      // Set security method to TOTP
      await setSecurityMethod.mutateAsync('totp');

      // Complete the setup (clear requires2FASetup flag)
      await complete2FASetup.mutateAsync();

      reset();
      setQrCode(null);
      setSecret(null);

      // Navigate to vault initialization if pending, otherwise to my-vaults
      if (user?.pendingVaultId) {
        navigate(`/vault/initialize/${user.pendingVaultId}`);
      } else {
        navigate('/my-vaults');
      }
    } catch {
      // Error handled by hooks
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex min-h-screen flex-col bg-neutral-100">
      {/* Header */}
      <header className="border-b border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-50">
              <Shield className="h-5 w-5 text-primary-600" />
            </div>
            <h1 className="text-lg font-semibold text-neutral-900">
              {t('auth.force2fa.pageTitle', 'Configuration de sécurité requise')}
            </h1>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
          >
            <LogOut className="h-4 w-4" />
            {t('common.logout', 'Déconnexion')}
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 items-center justify-center p-4">
        <div className="w-full max-w-4xl rounded-xl border border-neutral-200 bg-white">
          {/* Header */}
          <div className="border-b border-neutral-200 p-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
              <Shield className="h-8 w-8 text-primary-600" />
            </div>
            <h2 className="text-2xl font-bold text-neutral-900">
              {selectedMethod
                ? t('auth.force2fa.title', "Configuration de l'authentification à deux facteurs")
                : t('auth.force2fa.chooseTitle', 'Choisissez votre méthode de sécurité')}
            </h2>
            <p className="mt-2 text-sm text-neutral-600">
              {selectedMethod
                ? t(
                    'auth.force2fa.description',
                    'Pour accéder à votre coffre-fort sécurisé, vous devez configurer l\'authentification à deux facteurs (2FA). Cela protège vos documents sensibles.'
                  )
                : t(
                    'auth.force2fa.chooseDescription',
                    'Pour protéger votre compte, choisissez une méthode de vérification. Vous pourrez la modifier ultérieurement dans vos paramètres.'
                  )}
            </p>
          </div>

          {/* Method Selection */}
          {!selectedMethod && (
            <div className="p-6">
              <div className="grid gap-4 md:grid-cols-2">
                {/* TOTP Option */}
                <button
                  onClick={() => setSelectedMethod('totp')}
                  className="group rounded-xl border-2 border-neutral-200 p-6 text-left transition-all hover:border-primary-500 hover:shadow-md"
                >
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-50 transition-colors group-hover:bg-primary-100">
                    <Smartphone className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="mb-1 text-lg font-semibold text-neutral-900">
                    {t('auth.force2fa.totpTitle', 'Application d\'authentification')}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    {t('auth.force2fa.totpDescription', 'Utilisez Google Authenticator, Authy ou une autre application pour générer des codes de vérification.')}
                  </p>
                  <div className="mt-3 text-xs font-medium text-primary-600">
                    {t('auth.force2fa.recommended', 'Recommandé')}
                  </div>
                </button>

                {/* OTP Option */}
                <button
                  onClick={handleChooseOtp}
                  disabled={setSecurityMethod.isPending || complete2FASetup.isPending}
                  className="group rounded-xl border-2 border-neutral-200 p-6 text-left transition-all hover:border-primary-500 hover:shadow-md disabled:opacity-50"
                >
                  <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-success-50 transition-colors group-hover:bg-success-100">
                    <Mail className="h-6 w-6 text-success-600" />
                  </div>
                  <h3 className="mb-1 text-lg font-semibold text-neutral-900">
                    {t('auth.force2fa.otpTitle', 'Code par email')}
                  </h3>
                  <p className="text-sm text-neutral-600">
                    {t('auth.force2fa.otpDescription', 'Recevez un code de vérification par email à chaque connexion. Plus simple, aucune application requise.')}
                  </p>
                  {(setSecurityMethod.isPending || complete2FASetup.isPending) && (
                    <div className="mt-3 flex items-center gap-2 text-xs text-neutral-500">
                      <Loader2 className="h-3 w-3 animate-spin" />
                      {t('common.loading')}
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* QR Code Section */}
          {selectedMethod === 'totp' && qrCode && (
            <div className="p-6">
              <div className="grid gap-6 md:grid-cols-2">
                <div className="space-y-4">
                  {/* Step 1 */}
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        1
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step1', 'Téléchargez une application d\'authentification')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <Smartphone className="h-4 w-4" />
                      <span>{t('settings.twoFactor.downloadApp', 'Installez l\'une des applications suivantes')}</span>
                    </div>
                    <p className="mt-2 text-xs text-neutral-500">
                      Google Authenticator, Authy, Microsoft Authenticator
                    </p>
                  </div>

                  {/* Step 2 - QR Code */}
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        2
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step2', 'Scannez le QR code')}
                      </p>
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={qrCode}
                        alt="QR Code"
                        className="h-48 w-48 rounded-lg border border-neutral-300 bg-white p-2"
                      />
                    </div>
                    <div className="mt-3 rounded-lg border border-neutral-200 bg-white p-3">
                      <p className="mb-1 text-xs font-medium text-neutral-700">
                        {t('settings.twoFactor.manualEntry', 'Entrée manuelle')}
                      </p>
                      <div className="flex items-center justify-between gap-2">
                        <code className="flex-1 break-all font-mono text-sm text-neutral-900">
                          {secret}
                        </code>
                        <button
                          onClick={handleCopySecret}
                          className="rounded-md p-1.5 text-neutral-600 transition-colors hover:bg-neutral-100"
                        >
                          <Copy className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Step 3 - Verification */}
                <div className="flex flex-col justify-center">
                  <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                    <div className="mb-3 flex items-center gap-2">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-xs font-bold text-white">
                        3
                      </div>
                      <p className="font-medium text-neutral-900">
                        {t('settings.twoFactor.step3', 'Entrez le code de vérification')}
                      </p>
                    </div>

                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="mb-6">
                        <div className="relative">
                          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                            <KeyRound className="h-5 w-5 text-neutral-400" />
                          </div>
                          <input
                            {...register('token')}
                            type="text"
                            maxLength={6}
                            placeholder="000000"
                            className="block w-full rounded-lg border border-neutral-300 py-3 pl-10 pr-3 text-center text-2xl font-mono tracking-[0.5em] shadow-sm transition-colors focus:border-primary-500 focus:ring-2 focus:ring-primary-100 focus:outline-none"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            pattern="\d{6}"
                          />
                        </div>
                        {errors.token && (
                          <p className="mt-1 text-sm text-error-600">
                            {t(errors.token.message as string)}
                          </p>
                        )}
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting || enableTwoFactor.isPending || complete2FASetup.isPending}
                        className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary-600 px-4 py-3 text-base font-medium text-white shadow-sm transition-colors hover:bg-primary-700 focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
                      >
                        {isSubmitting || enableTwoFactor.isPending || complete2FASetup.isPending ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            {t('common.verifying', 'Vérification...')}
                          </>
                        ) : (
                          <>
                            <Check className="h-5 w-5" />
                            {t('auth.force2fa.enable', 'Activer le 2FA')}
                          </>
                        )}
                      </button>
                    </form>
                  </div>

                  {/* Security info */}
                  <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
                    <div className="flex items-start gap-3 text-xs text-primary-800">
                      <Lock className="mt-0.5 h-4 w-4 flex-shrink-0" />
                      <p>
                        {t(
                          'auth.force2fa.securityNote',
                          'L\'authentification à deux facteurs ajoute une couche de sécurité supplémentaire à votre compte. Vos documents ne seront accessibles qu\'après avoir validé cette étape.'
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading */}
          {selectedMethod === 'totp' && !qrCode && isSetupPending && (
            <div className="flex justify-center py-16">
              <div className="flex flex-col items-center gap-4">
                <Loader2 className="h-10 w-10 animate-spin text-primary-600" />
                <p className="text-sm text-neutral-600">
                  {t('auth.force2fa.loading', 'Préparation de la configuration...')}
                </p>
              </div>
            </div>
          )}

          {/* Error State */}
          {selectedMethod === 'totp' && !qrCode && isSetupError && (
            <div className="p-6">
              <div className="rounded-lg border border-error-200 bg-error-50 p-6 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-error-100">
                  <AlertTriangle className="h-6 w-6 text-error-600" />
                </div>
                <h3 className="mb-2 text-lg font-medium text-error-900">
                  {t('auth.messages.twoFactor.setupError', 'Erreur lors de la configuration')}
                </h3>
                <p className="mb-4 text-sm text-error-700">
                  {t(
                    'auth.force2fa.errorDescription',
                    'Une erreur est survenue lors de la génération du QR code. Veuillez réessayer.'
                  )}
                </p>
                <div className="flex justify-center gap-3">
                  <button
                    onClick={handleLogout}
                    className="rounded-lg border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
                  >
                    {t('common.logout', 'Déconnexion')}
                  </button>
                  <button
                    onClick={handleRetrySetup}
                    className="rounded-lg bg-error-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-error-700"
                  >
                    {t('common.retry', 'Réessayer')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};
